// Modern BaseBubble.js - Sleek, minimal message bubble
import React, { forwardRef, useState } from 'react';
import { IconButton, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import MessageOptionsMenu from './MessageOptionsMenu';

const BaseBubble = forwardRef(function BaseBubble({
  children,
  message,
  isUserMessage,
  isTcc,
  deleteMessageLoading,
  handleDeleteMessage,
  onReply,
  onQuote,
  onReact,
  onReactionAdd,
  onReactionRemove,
  conversationColor,
  customColors = false,
  padding,
  showOptionsMenu = true,
  ...props
}, ref) {
  const theme = useTheme();
  const accentColor = conversationColor || theme.palette.primary.main;

  // Modern BaseBubble.js - Sleek, minimal message bubble
  const bubbleStyles = {
    user: {
      background: `linear-gradient(135deg, ${accentColor}, ${alpha(accentColor, 0.86)})`,
      color: theme.palette.primary.contrastText || '#fff',
      borderRadius: '18px 18px 4px 18px',
      boxShadow: `0 2px 8px ${alpha(accentColor, 0.22)}`,
    },
    other: {
      background: theme.palette.mode === 'dark' 
        ? alpha(theme.palette.background.paper, 0.9)
        : "#fffffe",
      color: theme.palette.text.primary,
      borderRadius: '18px 18px 18px 4px',
      boxShadow: `0 1px 6px ${alpha(theme.palette.common.black, 0.08)}`,
    }
  };

  const currentStyle = isUserMessage ? bubbleStyles.user : bubbleStyles.other;

  return (
    <Box
      sx={{ 
        position: 'relative', 
        display: 'flex',
        justifyContent: isUserMessage ? 'flex-end' : 'flex-start',
        mb: 0,
        '&:hover .message-options': {
          opacity: 1,
          visibility: 'visible',
        }
      }}
    >
      <Box
        ref={ref}
        {...props}
        sx={{
          width: 'auto',
          maxWidth: { xs: '90%', sm: '85%', md: 'auto' },
          minWidth: '80px',
          px: 1.5,
          py: 0.9,
          position: "relative",
          display: 'inline-block',
          ...(!customColors && currentStyle),
          overflowWrap: "break-word",
          wordBreak: "normal",
          whiteSpace: "pre-wrap",
          transition: 'all 0.15s ease-in-out',
          ...props.sx
        }}
      >
      
        {children}

        {/* Message options - always rendered but controlled by CSS hover */}
        {showOptionsMenu && (
          <Box
            className="message-options"
            sx={{
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              opacity: 0,
              visibility: 'hidden',
              transition: 'opacity 0.2s, visibility 0.2s',
              // Position based on message type
              ...(isUserMessage 
                ? { right: '100%', mr: 1 }
                : { left: '100%', ml: 1 }
              ),
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
              // Larger clickable area
              p: 0.25,
              bgcolor: alpha(theme.palette.background.paper, 0.95),
              borderRadius: '20px',
              boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.15)}`,
            }}
          >
            <MessageOptionsMenu
              message={message}
              isUserMessage={isUserMessage}
              isTcc={isTcc}
              onReply={onReply}
              onQuote={onQuote}
              onReact={onReact}
              onReactionAdd={onReactionAdd}
              onReactionRemove={onReactionRemove}
              onDelete={handleDeleteMessage}
              deleteLoading={deleteMessageLoading}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
});

export default BaseBubble;
